/* eslint-disable react/no-unescaped-entities */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Mail, Phone, CheckCircle2, Loader2 } from 'lucide-react';
import api from '../../lib/api';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };

/**
 * QuickLeadForm — Capture lead express (email + téléphone optionnel).
 *
 * Réutilisable dans le hero, en sticky, dans la section finale ou
 * dans un widget flottant. Cible : convertir un visiteur en demande
 * de rappel/recontact sans le faire passer par le grand modal.
 *
 * Variants :
 *  - 'hero'     : version grande, label visible, à plat sur le fond
 *  - 'compact'  : version inline mince
 *  - 'card'     : version dans une carte avec background
 *
 * Backend : POST /api/contact/leadmagnet (champ email + first_name).
 * Le téléphone est concaténé au first_name pour récupérer la trace
 * (« Jean | 0612345678 ») — solution pragmatique sans modifier
 * le schéma backend.
 */
export const QuickLeadForm = ({
    variant = 'card',
    label,
    placeholder = 'Votre email professionnel',
    ctaLabel = 'Être recontacté',
    askPhone = true,
    source = 'quick_form',
    onSubmitted,
    ['data-testid']: testId,
}) => {
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || submitting) return;
        setSubmitting(true);
        setError('');
        try {
            await api.post('/contact/leadmagnet', {
                email: email.trim().toLowerCase(),
                first_name: phone ? `[${source}] tel: ${phone.trim()}` : `[${source}]`,
            });
            setSubmitted(true);
            if (onSubmitted) onSubmitted({ email, phone, source });
        } catch (err) {
            setError("Impossible d'envoyer. Réessayez ou utilisez le bouton de démo.");
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={
                    variant === 'compact'
                        ? 'flex items-center gap-2 text-sm text-emerald-300'
                        : 'rounded-2xl border border-emerald-400/30 bg-emerald-500/8 p-5 flex items-start gap-3'
                }
                data-testid={testId ? `${testId}-success` : 'quick-lead-form-success'}
            >
                <CheckCircle2 className={variant === 'compact' ? 'w-4 h-4 flex-shrink-0' : 'w-6 h-6 text-emerald-300 flex-shrink-0 mt-0.5'} />
                <div>
                    <p className="text-sm font-semibold text-emerald-300" style={DISPLAY}>
                        Merci, nous vous recontactons sous 24h.
                    </p>
                    {variant !== 'compact' && (
                        <p className="text-xs text-emerald-200/70 mt-1">
                            Un membre de l'équipe Homly va prendre contact à <strong className="text-white">{email}</strong>{phone ? ` ou au ${phone}` : ''}.
                        </p>
                    )}
                </div>
            </motion.div>
        );
    }

    const inputBase =
        'w-full px-4 py-3 rounded-full bg-white/[0.05] border border-white/10 focus:border-[#A855F7]/50 focus:bg-white/[0.08] outline-none text-sm text-white placeholder-white/35 transition-colors';

    const button = (
        <button
            type="submit"
            disabled={submitting || !email}
            data-testid={testId ? `${testId}-submit` : 'quick-lead-form-submit'}
            className="group inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-semibold text-sm shadow-xl shadow-[#681793]/30 hover:opacity-95 hover:scale-[1.01] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 whitespace-nowrap"
        >
            {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
                <>
                    {ctaLabel}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </>
            )}
        </button>
    );

    if (variant === 'compact') {
        return (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 w-full" data-testid={testId || 'quick-lead-form'}>
                <input
                    type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder={placeholder} aria-label="Email professionnel"
                    className={inputBase + ' flex-1'}
                    data-testid={testId ? `${testId}-email` : 'quick-lead-form-email'}
                />
                {button}
                {error && <p className="text-xs text-rose-300 mt-1 col-span-2">{error}</p>}
            </form>
        );
    }

    return (
        <form
            onSubmit={handleSubmit}
            className={
                variant === 'hero'
                    ? 'mx-auto max-w-2xl rounded-2xl border border-white/12 bg-white/[0.03] backdrop-blur-xl p-4 md:p-5 shadow-2xl shadow-black/30'
                    : 'rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:p-6'
            }
            data-testid={testId || 'quick-lead-form'}
        >
            {label && (
                <p className="text-sm text-white/90 mb-3 font-medium" style={DISPLAY}>
                    {label}
                </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                <div className="relative sm:col-span-1">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                    <input
                        type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                        placeholder={placeholder} aria-label="Email professionnel"
                        className={inputBase + ' pl-10'}
                        data-testid={testId ? `${testId}-email` : 'quick-lead-form-email'}
                    />
                </div>
                {askPhone && (
                    <div className="relative sm:col-span-1">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                        <input
                            type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                            placeholder="Téléphone (optionnel)" aria-label="Téléphone"
                            className={inputBase + ' pl-10'}
                            data-testid={testId ? `${testId}-phone` : 'quick-lead-form-phone'}
                        />
                    </div>
                )}
            </div>
            <div className="mt-3 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                <p className="text-[11px] text-white/40">
                    Réponse sous 24h ouvrées · Sans engagement
                </p>
                {button}
            </div>
            {error && <p className="text-xs text-rose-300 mt-3">{error}</p>}
        </form>
    );
};
